<script setup>
	import { onBeforeUnmount, watch } from "vue";

	const props = defineProps({
		modelValue: {
			type: Boolean,
			default: false,
		},
	});

	const emit = defineEmits(["update:modelValue"]);

	function close() {
		emit("update:modelValue", false);
	}

	function handleKeydown(event) {
		if (event.key === "Escape") {
			close();
		}
	}

	watch(() => props.modelValue, (isOpen) => {
		if (typeof document === "undefined") {
			return;
		}

		if (isOpen) {
			const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
			document.documentElement.style.overflow = "hidden";
			document.body.style.overflow = "hidden";

			if (scrollbarWidth > 0) {
				document.body.style.paddingRight = `${scrollbarWidth}px`;
			}

			window.addEventListener("keydown", handleKeydown);
		} else {
			document.documentElement.style.overflow = "";
			document.body.style.overflow = "";
			document.body.style.paddingRight = "";
			window.removeEventListener("keydown", handleKeydown);
		}
	});

	onBeforeUnmount(() => {
		if (typeof document !== "undefined") {
			document.documentElement.style.overflow = "";
			document.body.style.overflow = "";
			document.body.style.paddingRight = "";
			window.removeEventListener("keydown", handleKeydown);
		}
	});
</script>

<template>
	<Teleport to="body">
		<transition name="lux-dialog-fade">
			<div v-if="modelValue" class="lux-dialog-overlay" @click.self="close">
				<div class="lux-dialog-card" role="dialog" aria-modal="true" aria-labelledby="privacyDialogTitle" @click.stop>
					<button type="button" class="lux-dialog-close" aria-label="Close" @click="close">&times;</button>

					<div class="lux-dialog-scroll">
						<header class="lux-dialog-head">
							<span class="lux-dialog-eyebrow">Lucentary</span>
							<h2 id="privacyDialogTitle" class="lux-dialog-title">Privacy Policy</h2>
							<p class="lux-dialog-meta">Effective Date: October 29, 2024</p>
						</header>

						<div class="lux-dialog-body">
							<p>At Lucentary Academy Inc d/b/a Lucentary (hereinafter referred to as "we," "us," or "our"), we are committed to protecting your privacy. This Privacy Policy outlines how we collect, use, and safeguard your information when you visit our website, https://lucentary.com, and engage with our services.</p>

							<h3>Information We Collect</h3>
							<p>We may collect the following types of information:</p>
							<ul>
								<li><strong>Personal Information:</strong> This includes your name, email address, phone number, and any other information you provide when you register for our services or contact us.</li>
								<li><strong>Usage Data:</strong> We collect information about your interactions with our website, including your IP address, browser type, pages visited, and the time and date of your visit.</li>
								<li><strong>Cookies and Tracking Technologies:</strong> We use cookies and similar technologies to enhance your experience on our website. You can manage your cookie preferences through your browser settings.</li>
							</ul>

							<h3>How We Use Your Information</h3>
							<p>We use the information we collect for various purposes, including:</p>
							<ul>
								<li>To provide, maintain, and improve our services</li>
								<li>To communicate with you, including sending updates, newsletters, and promotional materials</li>
								<li>To respond to your inquiries and provide customer support</li>
								<li>To monitor usage and analyze trends to enhance user experience</li>
							</ul>

							<h3>Text Messaging Policy</h3>
							<p>By providing your phone number, you consent to receive text messages from us regarding your account, updates, and promotional offers.</p>
							<p><strong>Opt-In:</strong> You will receive a confirmation message after opting in to our text messaging program.</p>
							<p><strong>Opt-Out:</strong> If you wish to stop receiving text messages, you can opt-out at any time by replying "STOP" to any text message you receive from us. You may also contact us directly at info@lucentary.com to request removal from our text messaging list.</p>

							<h3>Sharing Your Information</h3>
							<p>We do not sell, trade, or otherwise transfer your personal information to outside parties without your consent, except to trusted third parties who assist us in operating our website and conducting our business, so long as those parties agree to keep this information confidential.</p>

							<h3>Data Security</h3>
							<p>We implement a variety of security measures to maintain the safety of your personal information. While we strive to protect your personal information, no method of transmission over the Internet or method of electronic storage is 100% secure. Therefore, we cannot guarantee its absolute security.</p>

							<h3>Your Rights</h3>
							<p>You have the right to:</p>
							<ul>
								<li>Access the personal information we hold about you</li>
								<li>Request corrections to any inaccuracies in your personal information</li>
								<li>Request deletion of your personal information, subject to certain exceptions</li>
							</ul>
							<p>To exercise these rights, please contact us at info@lucentary.com.</p>

							<h3>Changes to This Privacy Policy</h3>
							<p>We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on our website. Your continued use of our services after any modifications to the Privacy Policy will constitute your acknowledgment of the modifications and your consent to abide by and be bound by the modified policy.</p>

							<h3>Contact Us</h3>
							<p>If you have any questions about this Privacy Policy, please contact us at:</p>
							<address>
								Lucentary<br />
								Email: <a href="mailto:info@lucentary.com" target="_blank" rel="noopener">info@lucentary.com</a><br />
								Website: <a href="https://lucentary.com" target="_blank" rel="noopener">https://lucentary.com</a>
							</address>
						</div>
					</div>

					<footer class="lux-dialog-foot">
						<button type="button" class="lux-dialog-understand" @click="close">I understand</button>
					</footer>
				</div>
			</div>
		</transition>
	</Teleport>
</template>

<style scoped>
	.lux-dialog-overlay {
		position: fixed;
		inset: 0;
		z-index: 100000;
		display: flex;
		align-items: center;
		justify-content: center;
		padding: 1.5rem;
		background: rgba(10, 23, 48, 0.55);
		backdrop-filter: blur(5px);
		-webkit-backdrop-filter: blur(5px);
		cursor: pointer;
		font-family: "Poppins", "Segoe UI", -apple-system, BlinkMacSystemFont, sans-serif;
	}

	.lux-dialog-card {
		position: relative;
		display: flex;
		flex-direction: column;
		width: 100%;
		max-width: 640px;
		max-height: calc(100vh - 3rem);
		max-height: calc(100dvh - 3rem);
		background: #ffffff;
		border-radius: 20px;
		box-shadow: 0 40px 90px -30px rgba(10, 23, 48, 0.6);
		cursor: default;
		overflow: hidden;
	}

	.lux-dialog-close {
		position: absolute;
		top: 1rem;
		right: 1rem;
		z-index: 2;
		width: 40px;
		height: 40px;
		display: inline-flex;
		align-items: center;
		justify-content: center;
		border: none;
		border-radius: 12px;
		background: #f1f5fb;
		color: #475569;
		font-size: 1.8rem;
		line-height: 1;
		cursor: pointer;
		transition: background 0.18s ease, color 0.18s ease;
	}

	.lux-dialog-close:hover {
		background: #e7eefe;
		color: #1d4ed8;
	}

	.lux-dialog-scroll {
		flex: 1;
		overflow-y: auto;
		-webkit-overflow-scrolling: touch;
		padding: clamp(1.75rem, 4vw, 2.5rem);
		padding-top: clamp(2.25rem, 4vw, 2.75rem);
	}

	.lux-dialog-head {
		margin-bottom: 1.5rem;
		padding-right: 2.5rem;
	}

	.lux-dialog-eyebrow {
		display: inline-block;
		font-size: 0.78rem;
		font-weight: 700;
		letter-spacing: 0.14em;
		text-transform: uppercase;
		color: #2563eb;
		margin-bottom: 0.5rem;
	}

	.lux-dialog-title {
		font-size: clamp(1.6rem, 3vw, 2.1rem);
		font-weight: 800;
		letter-spacing: -0.01em;
		color: #0d1b34;
		margin: 0 0 0.35rem;
	}

	.lux-dialog-meta {
		font-size: 0.92rem;
		color: #6f7f99;
		margin: 0;
	}

	.lux-dialog-body {
		color: #45556f;
		font-size: 1rem;
		line-height: 1.7;
	}

	.lux-dialog-body h3 {
		font-size: 1.18rem;
		font-weight: 700;
		color: #0f2142;
		margin: 1.75rem 0 0.6rem;
	}

	.lux-dialog-body p {
		margin: 0 0 1rem;
	}

	.lux-dialog-body ul {
		margin: 0 0 1rem;
		padding-left: 1.3rem;
	}

	.lux-dialog-body li {
		margin-bottom: 0.5rem;
	}

	.lux-dialog-body strong {
		color: #0f2142;
		font-weight: 600;
	}

	.lux-dialog-body address {
		font-style: normal;
		line-height: 1.7;
		color: #45556f;
	}

	.lux-dialog-body a {
		color: #2563eb;
		font-weight: 600;
		text-decoration: none;
		cursor: pointer;
		transition: color 0.18s ease;
	}

	.lux-dialog-body a:hover {
		color: #1d4ed8;
		text-decoration: underline;
	}

	.lux-dialog-foot {
		flex-shrink: 0;
		display: flex;
		justify-content: flex-end;
		gap: 0.75rem;
		padding: 1.1rem clamp(1.75rem, 4vw, 2.5rem);
		border-top: 1px solid #e6edf8;
		background: #ffffff;
	}

	.lux-dialog-understand {
		display: inline-flex;
		align-items: center;
		justify-content: center;
		padding: 0.7rem 1.6rem;
		border: none;
		border-radius: 999px;
		background: #2563eb;
		color: #ffffff;
		font-family: inherit;
		font-size: 0.98rem;
		font-weight: 600;
		cursor: pointer;
		box-shadow: 0 12px 24px -12px rgba(37, 99, 235, 0.7);
		transition: transform 0.18s ease, background 0.18s ease, box-shadow 0.18s ease;
	}

	.lux-dialog-understand:hover {
		transform: translateY(-1px);
		background: #1d4ed8;
		box-shadow: 0 16px 30px -14px rgba(37, 99, 235, 0.75);
	}

	.lux-dialog-fade-enter-active,
	.lux-dialog-fade-leave-active {
		transition: opacity 0.25s ease;
	}

	.lux-dialog-fade-enter-active .lux-dialog-card,
	.lux-dialog-fade-leave-active .lux-dialog-card {
		transition: transform 0.25s ease, opacity 0.25s ease;
	}

	.lux-dialog-fade-enter-from,
	.lux-dialog-fade-leave-to {
		opacity: 0;
	}

	.lux-dialog-fade-enter-from .lux-dialog-card,
	.lux-dialog-fade-leave-to .lux-dialog-card {
		transform: translateY(14px) scale(0.985);
		opacity: 0;
	}

	@media (max-width: 560px) {
		.lux-dialog-foot {
			justify-content: stretch;
		}

		.lux-dialog-understand {
			width: 100%;
		}
	}
</style>